import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { FollowType } from "scmp-sdk";

import { useFallbackStorageValue } from "shared/hooks";
import { accountAtom } from "shared/lib/account";

import { useMyNewDailyFiveArticleIds } from "scmp-app/components/home/my-news-daily-five/recommend-articles/hooks";
import { userServiceAtom } from "scmp-app/lib/user-service/atoms";

import { useSnapshotState } from ".";
const followedThreshold = 3;

export const useMyNews = () => {
  const { follows } = useAtomValue(userServiceAtom);
  const { isLoggedIn } = useAtomValue(accountAtom);

  const { value: isVisitedMyNewsPage } = useFallbackStorageValue("isVisitedMyNewsPage", {
    initializeWithValue: true,
    parse: raw => raw,
  });

  const [dataReference] = useSnapshotState(follows);
  // Check the followed value by snapshot
  const initialHasEnoughFollowedItems = useMemo(
    () => Object.values(dataReference ?? {}).flat().length >= followedThreshold,
    [dataReference],
  );

  const isDataLoaded = follows !== undefined || !isLoggedIn;
  // Check the followed value by variable list
  const hasEnoughFollowedItems = useMemo(
    () => isDataLoaded && follows !== undefined && follows.length >= followedThreshold,
    [follows, isDataLoaded],
  );

  const followedIds = useMemo(() => {
    const mapping = follows
      ?.map(follow => follow.split(":"))
      .reduce(
        (mapping, [type, entityId]) => ({
          ...mapping,
          [type]: [...mapping[type as FollowType], entityId],
        }),
        {
          [FollowType.Author]: [] as string[],
          [FollowType.Section]: [] as string[],
          [FollowType.Topic]: [] as string[],
        },
      );

    return {
      authorIds: mapping?.[FollowType.Author] ?? [],
      sectionIds: mapping?.[FollowType.Section] ?? [],
      topicIds: mapping?.[FollowType.Topic] ?? [],
    };
  }, [follows]);

  const getMyNewDailyFiveArticleIds = useMyNewDailyFiveArticleIds(followedIds);

  return {
    followedIds,
    getMyNewDailyFiveArticleIds,
    hasEnoughFollowedItems,
    initialHasEnoughFollowedItems,
    isDataLoaded,
    isVisitedMyNewsPage,
  };
};
